import axios from 'axios';
import { captureException } from 'src/services/reporting';

import { addToast } from 'src/shared/components/ui';

interface ErrorResponse {
  [key: string]: any;
}

interface HandleAxiosErrorOptions {
  silent?: boolean; // If true, suppresses toast notifications
}

/**
 * Thin Error subclass that preserves the Axios `code` (e.g. `ERR_NETWORK`)
 * and HTTP `status` on the error thrown out of API fetchers. This lets
 * global React Query handlers (retry, QueryCache `onError`) distinguish
 * transient network failures from real 4xx/5xx responses without having
 * to keep the raw `AxiosError` around.
 */
export class ApiError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, options?: { code?: string; status?: number }) {
    super(message);
    this.name = 'ApiError';
    this.code = options?.code;
    this.status = options?.status;
  }
}

const getErrorMessage = (errorResponse: unknown): string => {
  // handle error response from backend
  // need to see other error responses from backend
  if (errorResponse) {
    const errorData = errorResponse as ErrorResponse;

    if (errorData?.non_field_errors) {
      return errorData.non_field_errors[0];
    }
  }

  return 'An unexpected error occurred';
};

const triggerDangerToast = (message: string) => {
  addToast({ color: 'danger', title: message });
};

const handleAxiosError = (
  error: unknown,
  options?: HandleAxiosErrorOptions
): Error => {
  const { silent = false } = options || {};

  if (axios.isAxiosError(error)) {
    const code = error.code;
    const status = error.response?.status;

    const extra = {
      statusCode: status,
      message: error.response?.data?.detail
    };

    // Don't Sentry-report transient network errors — they're almost always
    // driven by client-side conditions (tab freeze, flaky wifi, etc.) and
    // are retried automatically by the QueryClient.
    if (code !== 'ERR_NETWORK') {
      captureException({
        error,
        extra,
        level: status === 401 ? 'warning' : 'error'
      });
    }

    if (status === 400) {
      if (error?.response?.data?.detail) {
        if (!silent) {
          triggerDangerToast(error.response?.data?.detail);
        }

        return new ApiError(error.response?.data?.detail, { code, status });
      }

      if (error?.response?.data) {
        if (!silent) {
          triggerDangerToast(getErrorMessage(error.response?.data));
        }

        return new ApiError(getErrorMessage(error.response?.data), {
          code,
          status
        });
      }
    }

    if (error?.response?.data?.detail) {
      return new ApiError(error.response?.data?.detail, { code, status });
    }

    if (error?.response?.data) {
      return new ApiError(getErrorMessage(error.response?.data), {
        code,
        status
      });
    }

    return new ApiError('Failed to fetch data', { code, status });
  }

  return new ApiError('An unexpected error occurred');
};

export default handleAxiosError;
